import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { CloudUpload, Close } from '@mui/icons-material';

const ImageDropzone = ({ selectedFiles, onFilesChange, multiple = true, label = "Profile Images (Optional)" }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );

    if (files.length > 0) {
      if (multiple) {
        onFilesChange([...selectedFiles, ...files]);
      } else {
        onFilesChange([files[0]]);
      }
    }
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      if (multiple) {
        onFilesChange([...selectedFiles, ...files]);
      } else {
        onFilesChange([files[0]]);
      }
    }
  };

  const handleRemoveFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    onFilesChange(newFiles);
  };

  return (
    <Box sx={{ width: '100%', mt: 2, mb: 2 }}>
      <Typography variant="body2" sx={{ mb: 1, color: 'var(--text-secondary)', fontWeight: 500 }}>
        {label}
      </Typography>
      
      <Box
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          border: isDragging ? '2px dashed var(--app-accent)' : '2px dashed var(--border-color)',
          borderRadius: '12px',
          padding: '32px',
          textAlign: 'center',
          backgroundColor: isDragging ? 'rgba(255, 87, 34, 0.05)' : 'var(--bg-secondary)',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: 'var(--hover-bg)',
            borderColor: 'var(--app-accent)',
          }
        }}
        onClick={() => document.getElementById('dropzone-file-input').click()}
      >
        <CloudUpload sx={{ fontSize: 48, color: isDragging ? 'var(--app-accent)' : 'var(--text-tertiary)', mb: 2 }} />
        <Typography variant="body1" sx={{ mb: 1, fontWeight: 500, color: 'var(--text-primary)' }}>
          {isDragging ? 'Drop images here' : 'Drag & drop images here'}
        </Typography>
        <Typography variant="body2" sx={{ color: 'var(--text-tertiary)' }}>
          or click to browse
        </Typography>
        <input
          id="dropzone-file-input"
          type="file"
          accept="image/*"
          multiple={multiple}
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
      </Box>

      {selectedFiles.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
          {selectedFiles.map((file, index) => (
            <Box
              key={index}
              sx={{
                position: 'relative',
                width: 120,
                height: 120,
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              <img
                src={URL.createObjectURL(file)}
                alt={`Preview ${index + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFile(index);
                }}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  backgroundColor: 'rgba(0,0,0,0.6)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.8)',
                  },
                  padding: '4px',
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ImageDropzone;
